import { SubmitButton, Typography } from '@capsule/consumer-design-system';

import { Form, Formik } from 'formik';
import { css } from 'styled-components';

import ChevronIcon from 'public/new_chevron.svg';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { FormikPhoneField } from 'src/app/components/Form/FormikPhoneField/FormikPhoneField';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import type { FormValues } from 'src/app/components/Pages/LoggedInTransfer/Experiment/Doctor/validation';
import { validation } from 'src/app/components/Pages/LoggedInTransfer/Experiment/Doctor/validation';
import { useLoggedInTransferFormData } from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { showYourDoctor } from 'src/app/router/paths';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import type { RenderedStepComponentProps } from '..';
import Step from '../Step';

import { ShowYourDoctorLink, StyledArrow } from './styles';

export const LoggedInTransferDoctor = ({ handleNextClick }: RenderedStepComponentProps) => {
  const { loggedInTransferFormData, setLoggedInTransferFormData } = useLoggedInTransferFormData();

  useSegmentEvent('viewTransferDoctorInfo', { shouldTrackOnMount: true });

  const trackEventLoggedInTransferSubmit = useSegmentEvent('submitLoggedInTransfer', { shouldTrackOnMount: false });
  const trackEventDoctorNameFieldFocus = useSegmentEvent('focusedOnDoctorNameField', { shouldTrackOnMount: false });
  const trackEventDoctorPhoneFieldFocus = useSegmentEvent('focusedOnDoctorPhoneField', { shouldTrackOnMount: false });

  const setTransferFormValues = async (values: FormValues) => {
    await setLoggedInTransferFormData({
      ...loggedInTransferFormData,
      ...values,
    });
  };

  return (
    <Step title="Which doctor should we get your prescriptions from?" text="We’ll contact them on your behalf">
      <Formik
        validationSchema={validation.schema}
        initialValues={{
          ...validation.initialValues,
          ...loggedInTransferFormData,
        }}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          await setTransferFormValues(values);
          await trackEventLoggedInTransferSubmit();
          handleNextClick();
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${({ theme }) => theme.space.s4};
              `}
            >
              <FormikTextField
                adornment={{
                  left: (
                    <CapsuleIcon
                      name="stethoscope"
                      iconString={CAPSULE_ICON_STRINGS.stethoscope_icon}
                      css={css`
                        padding: 0 !important;
                        path {
                          fill: ${({ theme }) => theme.colors.capsuleGreen60};
                        }
                        svg {
                          width: 100% !important;
                          height: 100% !important;
                        }
                      `}
                    />
                  ),
                }}
                name="doctorName"
                label={{
                  isHidden: false,
                  content: 'Doctor or Practice Name',
                }}
                onFocus={() => trackEventDoctorNameFieldFocus()}
              />

              <FormikPhoneField
                adornment={{
                  left: (
                    <CapsuleIcon
                      name="phone"
                      iconString={CAPSULE_ICON_STRINGS.phone}
                      css={css`
                        padding: ${({ theme }) => theme.space.s1} !important;
                        path {
                          fill: ${({ theme }) => theme.colors.capsuleGreen60};
                        }
                        svg {
                          width: 100% !important;
                          height: 100% !important;
                        }
                      `}
                    />
                  ),
                }}
                name="doctorPhone"
                label={{ isHidden: false, content: `Doctor’s phone number` }}
                onFocus={() => trackEventDoctorPhoneFieldFocus()}
              />

              <ShowYourDoctorLink aria-label="Go To Show Your Doctor Page" to={showYourDoctor.toPath()} data-cy="logo">
                <Typography variant={'small'}>I’ll tell my doctor in person</Typography>
                <StyledArrow className="arrow" src={ChevronIcon} />
              </ShowYourDoctorLink>
            </div>

            <SubmitButton
              variant="contained-secondary-lg"
              isSubmitting={isSubmitting}
              css={css`
                margin-top: ${({ theme }) => theme.space.s7};
                width: 100%;
                max-width: 24rem;

                ${({ theme }) => theme.mediaQueries.smallDown} {
                  max-width: 100%;
                }
              `}
            >
              Continue
            </SubmitButton>
          </Form>
        )}
      </Formik>
    </Step>
  );
};
